import React, { useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import notifications from '../../notifications';
import { searchKids, addKidToProgram } from '../../api/admin';
import './AddKidForm.css';

const AddKidForm = ({ programId, programOrg, onSuccess, onCancel }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedKid, setSelectedKid] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    gender: '',
    contactFirstName: '',
    contactLastName: '',
    contactPhone: '',
    contactEmail: ''
  });

  // Debounced search function using ref to avoid dependency issues
  const debouncedSearchRef = useRef(
    debounce(async (firstName, lastName, setResults, setSearching) => {
      if (!firstName && !lastName) {
        setResults([]);
        return;
      }

      setSearching(true);
      try {
        const response = await searchKids(firstName, lastName);
        if (response.data) {
          setResults(response.data);
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearching(false);
      }
    }, 300)
  );

  useEffect(() => {
    if (!selectedKid) {
      debouncedSearchRef.current(formData.firstName, formData.lastName, setSearchResults, setIsSearching);
    }
    return () => debouncedSearchRef.current.cancel();
  }, [formData.firstName, formData.lastName, selectedKid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear selected kid when name changes
    if (name === 'firstName' || name === 'lastName') {
      setSelectedKid(null);
    }
  };

  const handleSelectKid = (kid) => {
    setSelectedKid(kid);
    setSearchResults([]);
    // Pre-fill form with selected kid's data for display
    setFormData({
      firstName: kid.firstName || '',
      lastName: kid.lastName || '',
      dob: kid.dob || '',
      gender: kid.gender || '',
      contactFirstName: kid.contactFirstName || '',
      contactLastName: kid.contactLastName || '',
      contactPhone: kid.contactPhone || '',
      contactEmail: kid.contactEmail || ''
    });
  };

  const handleClearSelection = () => {
    setSelectedKid(null);
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let payload;
      if (selectedKid) {
        // Add existing kid
        payload = { kidId: selectedKid.id };
      } else {
        // Create new kid - validate required fields
        if (!formData.firstName || !formData.lastName) {
          notifications.notifyError('First and last name are required');
          setIsSubmitting(false);
          return;
        }
        if (!formData.contactFirstName || !formData.contactLastName || !formData.contactEmail) {
          notifications.notifyError('Contact name and email are required');
          setIsSubmitting(false);
          return;
        }
        payload = { kid: formData };
      }

      const response = await addKidToProgram(programId, payload);
      if (!response.error && response.data) {
        notifications.notifySuccess(response.data.message || 'Kid added to program');
        onSuccess();
      } else {
        notifications.notifyError(response.error?.error || 'Failed to add kid');
      }
    } catch (error) {
      notifications.notifyError('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showCreateNewOption = formData.firstName && formData.lastName && !selectedKid;

  return (
    <div className="add-kid-form">
      <div className="form-header">
        <h4>Add Kid to Roster</h4>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Name Fields - Always shown */}
        <div className="form-row">
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
              disabled={!!selectedKid}
            />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
              disabled={!!selectedKid}
            />
          </div>
        </div>

        {/* Search Results Dropdown */}
        {!selectedKid && searchResults.length > 0 && (
          <div className="search-results">
            <div className="search-results-header">
              {isSearching ? 'Searching...' : `Found ${searchResults.length} matching kid(s)`}
            </div>
            <div className="search-results-list">
              {searchResults.map((kid) => (
                <div
                  key={kid.id}
                  className="search-result-item"
                  onClick={() => handleSelectKid(kid)}
                >
                  <span className="kid-name">{kid.firstName} {kid.lastName}</span>
                  <span className="kid-info">
                    {kid.dob && `DOB: ${kid.dob}`}
                    {kid.contactEmail && ` | ${kid.contactEmail}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Kid Banner */}
        {selectedKid && (
          <div className="selected-kid-banner">
            <span>Selected: <strong>{selectedKid.firstName} {selectedKid.lastName}</strong></span>
            <button type="button" onClick={handleClearSelection}>
              Clear
            </button>
          </div>
        )}

        {/* Create New Kid Option */}
        {showCreateNewOption && !showAllFields && (
          <div className="create-new-section">
            <p className="create-new-text">
              {searchResults.length > 0
                ? "Not finding the right kid?"
                : "No matches found."}
            </p>
            <button
              type="button"
              className="create-new-btn"
              onClick={() => setShowAllFields(true)}
            >
              Create new kid with this name
            </button>
          </div>
        )}

        {/* Additional Fields for Creating New Kid */}
        {showAllFields && !selectedKid && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Gender</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Contact First Name *</label>
                <input
                  type="text"
                  name="contactFirstName"
                  value={formData.contactFirstName}
                  onChange={handleChange}
                  placeholder="Parent/guardian first name"
                />
              </div>
              <div className="form-group">
                <label>Contact Last Name *</label>
                <input
                  type="text"
                  name="contactLastName"
                  value={formData.contactLastName}
                  onChange={handleChange}
                  placeholder="Parent/guardian last name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Contact Phone</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  placeholder="(555) 555-5555"
                />
              </div>
              <div className="form-group">
                <label>Contact Email *</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  placeholder="parent@email.com"
                />
              </div>
            </div>
          </>
        )}

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting || (!selectedKid && !showAllFields)}
          >
            {isSubmitting
              ? 'Adding...'
              : selectedKid
                ? 'Add Existing Kid'
                : 'Create & Add Kid'
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddKidForm;
